/**
 * Consultor digital — enriquecimento completo (100 rotinas).
 * Fase 1: rotinas prioritárias · Fase 2: demais rotinas da biblioteca.
 */

import { LEAN_LIBRARY_ENRICHMENT_PHASE2 } from './leanLibraryEnrichmentPhase2';

import type {
  LeanFrequenciaLabel,
  LeanIelImpactLevel,
  LeanLibraryEnrichment,
  LeanNivel,
  LeanRoutineMaturity,
} from '../types/leanLibrary';

type Iel = {
  disciplina: LeanIelImpactLevel;
  aderenciaPadrao: LeanIelImpactLevel;
  resultado: LeanIelImpactLevel;
};

type Freq = Partial<Record<LeanNivel, LeanFrequenciaLabel>>;

function e(
  swiPassos: string[],
  itensConformidade: string[],
  indicadoresPrimarios: string[],
  indicadoresSecundarios: string[],
  relacaoCausal: string,
  frequenciaBenchmark: Freq,
  nivelMaturidade: LeanRoutineMaturity,
  ielImpacto: Iel,
): LeanLibraryEnrichment {
  return {
    swiPassos,
    itensConformidade,
    indicadoresPrimarios,
    indicadoresSecundarios,
    relacaoCausal,
    frequenciaBenchmark,
    nivelMaturidade,
    ielImpacto,
  };
}

const L1 = 'nivel-1-lider-producao' as LeanNivel;
const L2 = 'nivel-2-supervisor' as LeanNivel;
const L3 = 'nivel-3-coordenador' as LeanNivel;
const L4 = 'nivel-4-gerente' as LeanNivel;
const L5 = 'nivel-5-diretor' as LeanNivel;

export const LEAN_LIBRARY_ENRICHMENT: Record<string, LeanLibraryEnrichment> = {
  'n1-d-01': e(
    [
      'Reunir líderes de turno entrante e sainte no ponto de passagem.',
      'Revisar indicadores do turno (OEE, qualidade, segurança, hora a hora).',
      'Listar pendências, anomalias abertas e restrições materiais.',
      'Definir TOP 3 prioridades do turno entrante.',
      'Registrar ata de passagem com assinaturas e prazos.',
    ],
    [
      'Indicadores do turno revisados e compreendidos',
      'Pendências críticas escaladas ou com dono',
      'Prioridades do turno definidas e comunicadas',
      'Ata de passagem completa e assinada',
    ],
    ['Continuidade operacional', 'Pendências abertas na passagem'],
    ['OEE do turno', 'Anomalias não tratadas'],
    'Passagem estruturada reduz perda de contexto entre turnos e evita repetição de falhas — impacta disponibilidade e disciplina operacional.',
    { [L1]: 'Diária', [L2]: 'Diária', [L3]: 'Semanal' },
    'basica',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n1-d-03': e(
    [
      'Abrir matriz de polivalência atualizada da linha/célula.',
      'Conferir alocação real vs matriz planejada por estação.',
      'Identificar gaps de competência ou ausências não cobertas.',
      'Acionar realocação ou treinamento imediato se necessário.',
      'Registrar desvios e plano de cobertura no turno.',
    ],
    [
      'Operadores alocados conforme matriz aprovada',
      'Competência mínima garantida em cada estação',
      'Gaps de polivalência identificados e tratados',
      'Matriz atualizada com desvios do turno',
    ],
    ['Cobertura de competências', 'Gaps de polivalência'],
    ['Produtividade', 'Retrabalho por erro de alocação'],
    'Cobertura adequada de competências reduz variabilidade e retrabalho — protege produtividade e qualidade.',
    { [L1]: 'Diária', [L2]: 'Semanal', [L3]: 'Mensal' },
    'basica',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'medio' },
  ),

  'n1-d-04': e(
    [
      'Preparar pauta com indicadores, anomalias e TOP 3 riscos do turno.',
      'Conduzir reunião em até 15 min com equipe de líderes.',
      'Revisar status de ações da reunião anterior.',
      'Definir responsáveis e prazos para impedimentos imediatos.',
      'Registrar ata e comunicar escalonamentos necessários.',
    ],
    [
      'Indicadores críticos do turno apresentados',
      'Impedimentos com dono e prazo definidos',
      'Ações anteriores revisadas',
      'Ata registrada com TOP 3 prioridades',
    ],
    ['Ações abertas da camada', 'Impedimentos resolvidos no turno'],
    ['OEE', 'Tempo de resposta a anomalias'],
    'Reuniões de camada sincronizam execução e removem barreiras — aceleram resposta operacional e protegem meta do turno.',
    { [L1]: 'Diária', [L2]: 'Diária', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n1-d-05': e(
    [
      'Abrir quadro hora a hora do turno com plano vs real.',
      'Identificar horas com desvio de ritmo ou meta.',
      'Investigar causa raiz imediata com operador/líder de estação.',
      'Registrar causa e ação de recuperação no quadro.',
      'Escalar se desvio persistir por 2+ horas consecutivas.',
    ],
    [
      'Quadro hora a hora atualizado',
      'Desvios com causa registrada',
      'Plano de recuperação definido para horas críticas',
      'Meta do turno visível e compreendida',
    ],
    ['Aderência hora a hora', 'Desvios de ritmo'],
    ['OEE', 'Backlog do turno'],
    'Controle hora a hora detecta desvios cedo — evita perda acumulada de produção e facilita recuperação dentro do turno.',
    { [L1]: 'Diária', [L2]: 'Diária', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n1-d-06': e(
    [
      'Coletar dados de disponibilidade, performance e qualidade do turno.',
      'Calcular OEE e comparar com meta e turno anterior.',
      'Identificar maior perda (A, P ou Q) via pareto rápido.',
      'Definir ação de recuperação para a maior perda.',
      'Registrar OEE e plano no painel da área.',
    ],
    [
      'Dados de A, P e Q coletados corretamente',
      'OEE calculado e comparado à meta',
      'Maior perda identificada com causa',
      'Plano de recuperação documentado',
    ],
    ['OEE', 'Disponibilidade', 'Performance', 'Qualidade (FTR)'],
    ['Tempo de parada', 'Scrap', 'Retrabalho'],
    'OEE integra disponibilidade, performance e qualidade — foco nas três perdas direciona ações de maior impacto no resultado.',
    { [L1]: 'Diária', [L2]: 'Diária', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n1-d-07': e(
    [
      'Definir rota e foco do gemba (posto, fluxo ou problema específico).',
      'Observar execução real vs trabalho padrão documentado.',
      'Engajar operador com perguntas abertas (não auditoria punitiva).',
      'Registrar observações, desvios e oportunidades de melhoria.',
      'Abrir ações com dono e prazo; revisar na próxima caminhada.',
    ],
    [
      'Trabalho padrão visível e acessível no posto',
      'Execução observada vs padrão documentado',
      'Desvios registrados com evidência',
      'Ações de melhoria com responsável',
    ],
    ['Desvios identificados no gemba', 'Ações abertas pós-gemba'],
    ['Aderência ao padrão', 'Tempo de ciclo'],
    'Gemba revela gap entre padrão e realidade — base para melhoria contínua e aderência operacional.',
    { [L1]: 'Diária', [L2]: 'Diária', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'alto' },
  ),

  'n1-d-08': e(
    [
      'Selecionar estação/posto e revisar trabalho padrão vigente.',
      'Observar sequência, método e tempos de execução.',
      'Comparar com padrão documentado (SWI, vídeo ou folheto).',
      'Registrar conformidades e não conformidades por item.',
      'Acionar correção ou retreinamento imediato se NC crítica.',
    ],
    [
      'Sequência correta',
      'Método correto',
      'Tempos respeitados',
      'Padrão disponível e legível no posto',
    ],
    ['Aderência ao padrão', 'Produtividade'],
    ['Retrabalho', 'Eficiência'],
    'Maior aderência ao padrão reduz variabilidade operacional — impacta produtividade, qualidade e previsibilidade do fluxo.',
    { [L1]: 'Diária', [L2]: 'Diária', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'alto' },
  ),

  'n1-d-09': e(
    [
      'Percorrer área com checklist de segurança (comportamentos e condições).',
      'Verificar EPIs, sinalização, bloqueios e housekeeping crítico.',
      'Identificar near misses e condições inseguras.',
      'Acionar contenção imediata para risco alto.',
      'Registrar achados e abrir tratativas com prazo.',
    ],
    [
      'EPIs utilizados corretamente',
      'Condições inseguras eliminadas ou isoladas',
      'Sinalização e bloqueios conformes',
      'Near misses registrados e tratados',
    ],
    ['Near misses', 'Condições inseguras abertas'],
    ['Acidentes', 'Taxa de conformidade EPI'],
    'Condições seguras sustentam produção — near misses tratados evoluem para zero acidentes e protegem disponibilidade.',
    { [L1]: 'Diária', [L2]: 'Diária', [L3]: 'Semanal' },
    'basica',
    { disciplina: 'alto', aderenciaPadrao: 'alto', resultado: 'muito_alto' },
  ),

  'n1-d-10': e(
    [
      'Avaliar organização (Seiri/Sort e Seiton/Set in Order).',
      'Avaliar limpeza e estado visual (Seiso/Shine).',
      'Verificar identificação e padronização (Seiketsu/Standardize).',
      'Confirmar descarte e itens fora do padrão (Shitsuke/Sustain).',
      'Registrar nota 5S e desvios com foto se necessário.',
    ],
    [
      'Organização conforme padrão 5S',
      'Limpeza e estado visual adequados',
      'Identificação clara de materiais e ferramentas',
      'Itens desnecessários removidos',
      'Desvios registrados com plano de correção',
    ],
    ['Nota 5S', 'Não conformidades 5S'],
    ['Tempo de procura', 'Acidentes', 'Quase acidentes'],
    'Ambiente 5S reduz tempo de procura e riscos de segurança — sustenta produtividade e qualidade no posto.',
    { [L1]: 'Diária', [L2]: 'Semanal', [L3]: 'Mensal' },
    'basica',
    { disciplina: 'alto', aderenciaPadrao: 'muito_alto', resultado: 'medio' },
  ),

  'n1-d-11': e(
    [
      'Revisar pontos de controle e padrões de qualidade da linha.',
      'Auditar execução de checks e registros de qualidade.',
      'Verificar contenções ativas para riscos conhecidos.',
      'Registrar NCs com severidade e localização.',
      'Acionar contenção e escalonamento para escapes críticos.',
    ],
    [
      'Pontos de controle executados conforme padrão',
      'Registros de qualidade completos e legíveis',
      'Contenções ativas onde aplicável',
      'NCs classificadas e com dono',
    ],
    ['Taxa de defeitos', 'NCs abertas'],
    ['Scrap', 'Retrabalho', 'Reclamações de cliente'],
    'Qualidade na fonte evita cascata de defeitos — reduz scrap, retrabalho e impacto no OEE.',
    { [L1]: 'Diária', [L2]: 'Diária', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'muito_alto' },
  ),

  'n1-d-12': e(
    [
      'Identificar anomalia (andon, gemba ou indicador).',
      'Acionar contenção imediata para proteger cliente/processo.',
      'Registrar anomalia com descrição, hora e evidência.',
      'Definir dono, prazo e nível de escalonamento.',
      'Confirmar contenção eficaz antes de encerrar.',
    ],
    [
      'Contenção aplicada antes de continuar produção',
      'Registro de anomalia completo',
      'Dono e prazo definidos',
      'Escalonamento acionado se fora do alcance da camada',
    ],
    ['Tempo de resposta a anomalias', 'Anomalias abertas'],
    ['OEE', 'Scrap por escape'],
    'Resposta rápida a anomalias evita normalização de falhas — protege qualidade e disponibilidade.',
    { [L1]: 'Diária', [L2]: 'Diária', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'alto', resultado: 'alto' },
  ),

  'n1-d-14': e(
    [
      'Confirmar preparação externa (materiais, ferramentas, setup sheet).',
      'Cronometrar setup e comparar com tempo padrão.',
      'Observar sequência interna vs padrão SMED documentado.',
      'Verificar checklist de setup preenchido.',
      'Registrar ajustes, perdas e oportunidades de redução.',
    ],
    [
      'Ferramentas preparadas antes do início',
      'Sequência seguida conforme padrão SMED',
      'Checklist de setup preenchido',
      'Ajustes e tempos registrados',
    ],
    ['Tempo médio de setup', '% setups dentro da meta'],
    ['Disponibilidade', 'OEE'],
    'Redução do setup aumenta disponibilidade e impacta diretamente o OEE — menor tempo morto entre lotes.',
    { [L1]: 'Diária', [L2]: 'Semanal', [L3]: 'Mensal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'alto' },
  ),

  'n1-s-02': e(
    [
      'Abrir painel de planos de ação da área (abertos e atrasados).',
      'Revisar status, evidências e prazos com responsáveis.',
      'Repriorizar ações críticas e remover bloqueios.',
      'Fechar ações concluídas com verificação de eficácia.',
      'Atualizar % de conclusão e comunicar pendências.',
    ],
    [
      'Status de cada ação atualizado',
      'Ações atrasadas com novo prazo ou escalonamento',
      'Evidência de conclusão verificada',
      'Painel reflete realidade da área',
    ],
    ['% conclusão de planos de ação', 'Ações atrasadas'],
    ['Recorrência de problemas', 'Tempo médio de fechamento'],
    'Follow-up rigoroso fecha o ciclo PDCA — evita acúmulo de pendências que degradam performance.',
    { [L1]: 'Semanal', [L2]: 'Semanal', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n1-s-04': e(
    [
      'Selecionar equipamentos da rota TPM da semana.',
      'Verificar limpeza, lubrificação e inspeção autônoma.',
      'Conferir etiquetagem e registros de anomalias de máquina.',
      'Validar tratamento de anomalias abertas.',
      'Registrar checklist TPM e escalar manutenção se necessário.',
    ],
    [
      'Limpeza conforme padrão TPM',
      'Lubrificação executada nos pontos definidos',
      'Inspeção autônoma realizada',
      'Etiquetagem visível e atualizada',
      'Anomalias registradas e encaminhadas',
    ],
    ['MTBF', 'Disponibilidade'],
    ['MTTR', 'Quebras não programadas'],
    'TPM autônomo aumenta disponibilidade — menos quebras e paradas impactam OEE e produtividade.',
    { [L1]: 'Semanal', [L2]: 'Semanal', [L3]: 'Mensal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'alto' },
  ),

  'n1-s-06': e(
    [
      'Coletar ideias de melhoria da equipe (gemba, sugestões, perdas).',
      'Priorizar por impacto x esforço (matriz simples).',
      'Selecionar 1–3 kaizens para execução na semana.',
      'Definir dono, prazo e indicador de ganho esperado.',
      'Registrar no painel de melhoria contínua.',
    ],
    [
      'Ideias registradas e classificadas',
      'Kaizens priorizados com critério objetivo',
      'Responsáveis e prazos definidos',
      'Ganho esperado estimado',
    ],
    ['Kaizens implementados', 'Ganho de produtividade'],
    ['Engajamento da equipe', 'Sustentação pós-kaizen'],
    'Kaizen sistemático converte observações do gemba em ganhos mensuráveis — eleva produtividade e engajamento.',
    { [L1]: 'Semanal', [L2]: 'Semanal', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'alto' },
  ),

  'n1-s-08': e(
    [
      'Preparar pauta com escalonamentos pendentes e KPIs da semana.',
      'Apresentar TOP 3 obstáculos ao supervisor.',
      'Revisar status de ações escaladas anteriormente.',
      'Obter decisão ou suporte da camada 2.',
      'Registrar ata e comunicar retorno à equipe.',
    ],
    [
      'Obstáculos escalados documentados',
      'Decisões da camada 2 registradas',
      'Ações anteriores revisadas',
      'Retorno comunicado aos líderes',
    ],
    ['Escalonamentos resolvidos', 'Tempo de resposta camada 2'],
    ['OEE', 'Ações atrasadas'],
    'Escalonamento eficaz remove barreiras fora do alcance da camada 1 — destrava fluxo e protege meta.',
    { [L1]: 'Semanal', [L2]: 'Semanal', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n2-d-01': e(
    [
      'Consolidar indicadores e riscos reportados pelos líderes.',
      'Conduzir reunião camada 2 em até 20 min.',
      'Priorizar TOP 3 riscos do turno/dia.',
      'Atribuir donos para barreiras cross-líder.',
      'Registrar ata e escalonar para coordenação se necessário.',
    ],
    [
      'KPIs consolidados apresentados',
      'Riscos priorizados com critério',
      'Barreiras com dono e prazo',
      'Ata camada 2 completa',
    ],
    ['Riscos mitigados no turno', 'Barreiras removidas'],
    ['OEE consolidado', 'Anomalias escaladas'],
    'Sincronização entre líderes evita silos — acelera resposta sistêmica a problemas operacionais.',
    { [L1]: 'Diária', [L2]: 'Diária', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n2-d-02': e(
    [
      'Definir roteiro multi-linha e critérios de observação.',
      'Executar gemba em 2–4 linhas com foco em padrão e segurança.',
      'Validar aderência TPL dos líderes indiretamente.',
      'Consolidar achados por líder e por tema.',
      'Distribuir ações e revisar na próxima caminhada.',
    ],
    [
      'Padrões operacionais observados vs documentados',
      'Desvios registrados por linha/líder',
      'Segurança e qualidade verificadas',
      'Plano de ação por líder definido',
    ],
    ['Desvios por líder', 'Aderência ao padrão (amostra)'],
    ['OEE por linha', 'NCs identificadas'],
    'Gemba em escala revela padrões sistêmicos — direciona coaching e priorização de recursos.',
    { [L2]: 'Diária', [L3]: 'Semanal', [L4]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'alto' },
  ),

  'n2-d-05': e(
    [
      'Revisar calendário TPL e execuções registradas dos líderes.',
      'Auditar amostra de rotinas críticas (presença + conformidade).',
      'Verificar qualidade das evidências registradas.',
      'Calcular score de aderência por líder.',
      'Feedback imediato e plano de desenvolvimento se gap.',
    ],
    [
      'Calendário TPL reflete execução real',
      'Evidências completas e auditáveis',
      'Score de aderência calculado',
      'Gaps com plano de correção',
    ],
    ['Aderência TPL', 'Disciplina de liderança'],
    ['IEL por líder', 'Conformidade média da área'],
    'Disciplina de liderança cascateia para a operação — TPL auditado eleva conformidade e resultado da área.',
    { [L2]: 'Diária', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'muito_alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n2-s-02': e(
    [
      'Coletar dados de perdas (parada, setup, defeito, espera).',
      'Classificar perdas por categoria e quantificar em minutos/unidades.',
      'Montar pareto das maiores perdas da semana.',
      'Selecionar top 3 para ataque com dono e meta.',
      'Registrar no painel de perdas operacionais.',
    ],
    [
      'Perdas quantificadas com método consistente',
      'Pareto reflete dados reais do período',
      'Top 3 perdas com plano de ação',
      'Baseline documentado para comparação',
    ],
    ['Perdas totais (min/un)', 'Top 3 perdas'],
    ['OEE', 'Custo de não qualidade'],
    'Atacar as maiores perdas primeiro maximiza ROI de melhoria — conecta Lean a resultado financeiro.',
    { [L2]: 'Semanal', [L3]: 'Semanal', [L4]: 'Mensal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n2-s-03': e(
    [
      'Abrir painel de NCs abertas e tendência semanal.',
      'Revisar eficácia de ações corretivas fechadas.',
      'Identificar NCs recorrentes (mesma causa/família).',
      'Escalar NCs críticas para análise estruturada (8D/A3).',
      'Atualizar status e prazos com donos.',
    ],
    [
      'NCs classificadas por severidade',
      'Ações corretivas com evidência de eficácia',
      'Recorrências identificadas',
      'Painel atualizado e confiável',
    ],
    ['NCs abertas', 'Taxa de recorrência'],
    ['Scrap', 'Custo de qualidade'],
    'Fechar loop de NCs evita repetição de defeitos — reduz custo de qualidade e protege cliente.',
    { [L2]: 'Semanal', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'muito_alto' },
  ),

  'n2-s-04': e(
    [
      'Listar paradas e falhas críticas da semana.',
      'Quantificar impacto em horas perdidas e OEE.',
      'Analisar causa raiz preliminar de cada evento.',
      'Priorizar falhas recorrentes para plano corretivo.',
      'Registrar e acompanhar plano com manutenção/engenharia.',
    ],
    [
      'Eventos críticos documentados com tempo de parada',
      'Impacto no OEE quantificado',
      'Causas preliminares registradas',
      'Planos corretivos com prazo',
    ],
    ['Disponibilidade', 'Paradas críticas'],
    ['MTBF', 'MTTR', 'OEE'],
    'Falhas críticas concentram perda de disponibilidade — tratamento estruturado eleva OEE rapidamente.',
    { [L2]: 'Semanal', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n2-s-06': e(
    [
      'Revisar matriz de polivalência consolidada da supervisão.',
      'Identificar estações single-point-of-failure (uma pessoa).',
      'Cruzar gaps com plano de treinamento e ausências previstas.',
      'Definir ações de desenvolvimento para próximo ciclo.',
      'Atualizar matriz e comunicar riscos aos líderes.',
    ],
    [
      'Cobertura mínima garantida por estação crítica',
      'Gaps de competência mapeados',
      'Plano de treinamento alinhado aos gaps',
      'Matriz revisada e publicada',
    ],
    ['Índice de polivalência', 'Estações sem cobertura'],
    ['Absenteísmo impacto', 'Produtividade'],
    'Polivalência adequada absorve variabilidade de pessoas — protege throughput e reduz risco operacional.',
    { [L1]: 'Diária', [L2]: 'Semanal', [L3]: 'Mensal' },
    'basica',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'medio' },
  ),

  'n2-s-07': e(
    [
      'Listar kaizens implementados nas últimas 4–8 semanas.',
      'Verificar se padrão novo está documentado e visível.',
      'Observar execução real vs padrão pós-kaizen.',
      'Registrar regressões e acionar re-sustentação.',
      'Celebrar kaizens sustentados; escalar lições aprendidas.',
    ],
    [
      'Padrão pós-kaizen documentado no posto',
      'Execução conforme novo padrão',
      'Indicador de ganho mantido',
      'Regressões tratadas',
    ],
    ['Taxa de sustentação kaizen', 'Ganho mantido'],
    ['Retrabalho', 'Produtividade'],
    'Sustentação evita retrocesso pós-melhoria — garante que ganhos de kaizen permaneçam no resultado.',
    { [L2]: 'Semanal', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'medio', aderenciaPadrao: 'muito_alto', resultado: 'alto' },
  ),

  'n2-s-10': e(
    [
      'Consolidar KPIs da semana por linha/líder.',
      'Conduzir fórum de performance com pauta objetiva.',
      'Comparar real vs meta e vs semana anterior.',
      'Definir foco e metas da próxima semana.',
      'Registrar ata com responsáveis e indicadores-alvo.',
    ],
    [
      'KPIs apresentados com dados confiáveis',
      'Gaps vs meta explicados com causa',
      'Metas da próxima semana definidas',
      'Ata com responsáveis',
    ],
    ['Atingimento de meta semanal', 'Tendência de KPIs'],
    ['OEE', 'Scrap', 'Segurança'],
    'Fórum de performance alinha foco coletivo — converte dados em ações prioritárias da semana.',
    { [L2]: 'Semanal', [L3]: 'Semanal' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n2-s-11': e(
    [
      'Preparar escalonamentos pendentes e KPIs consolidados.',
      'Apresentar TOP 3 obstáculos estruturais ao coordenador.',
      'Revisar decisões da camada 3 anteriores.',
      'Obter direcionamento ou recursos adicionais.',
      'Registrar ata e desdobrar para líderes.',
    ],
    [
      'Obstáculos estruturais documentados',
      'Decisões da coordenação registradas',
      'Recursos ou prazos acordados',
      'Retorno comunicado à camada 1',
    ],
    ['Escalonamentos resolvidos camada 3', 'Tempo de resposta'],
    ['OEE área', 'Investimentos pendentes'],
    'Camada 3 destrava problemas estruturais — acelera decisões que a supervisão não pode tomar sozinha.',
    { [L2]: 'Semanal', [L3]: 'Semanal', [L4]: 'Semanal' },
    'intermediaria',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'alto' },
  ),

  'n3-s-04': e(
    [
      'Revisar portfólio de projetos kaizen da coordenação.',
      'Verificar status, ganhos realizados e riscos de atraso.',
      'Priorizar projetos com maior impacto no OEE/custo.',
      'Remover barreiras cross-área dos projetos críticos.',
      'Atualizar painel e comunicar decisões.',
    ],
    [
      'Status de cada projeto atualizado',
      'Ganhos quantificados onde aplicável',
      'Projetos críticos com plano de recuperação',
      'Barreiras cross-área endereçadas',
    ],
    ['Ganho acumulado kaizen', 'Projetos no prazo'],
    ['ROI de melhoria', 'Engajamento'],
    'Gestão de portfólio kaizen garante foco em iniciativas de maior retorno — escala melhoria contínua.',
    { [L3]: 'Semanal', [L4]: 'Semanal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'muito_alto' },
  ),

  'n3-s-05': e(
    [
      'Revisar A3 abertos: problema, análise, contramedidas, follow-up.',
      'Validar rigor da análise de causa raiz.',
      'Confirmar contramedidas com dono, prazo e indicador.',
      'Fechar A3 com verificação de eficácia quando aplicável.',
      'Escalar A3 crônicos para revisão gerencial.',
    ],
    [
      'Análise de causa raiz documentada',
      'Contramedidas SMART definidas',
      'Indicador de eficácia monitorado',
      'A3 atualizado no prazo',
    ],
    ['A3 fechados no prazo', 'Eficácia de contramedidas'],
    ['Recorrência do problema', 'Perdas associadas'],
    'A3 estruturado elimina causas raiz — evita remédios paliativos e reduz problemas crônicos.',
    { [L3]: 'Semanal', [L4]: 'Semanal' },
    'avancada',
    { disciplina: 'medio', aderenciaPadrao: 'alto', resultado: 'muito_alto' },
  ),

  'n4-s-01': e(
    [
      'Consolidar KPIs táticos e escalonamentos das coordenações.',
      'Conduzir fórum gerencial com coordenadores.',
      'Priorizar decisões de alocação, investimento ou política.',
      'Revisar status de ações estratégico-táticas.',
      'Registrar ata e comunicar direcionadores.',
    ],
    [
      'KPIs táticos consolidados apresentados',
      'Decisões gerenciais registradas',
      'Ações estratégico-táticas revisadas',
      'Prioridades desdobradas',
    ],
    ['Atingimento meta unidade', 'Decisões implementadas'],
    ['OEE planta', 'Custos operacionais'],
    'Camada gerencial alinha coordenações — evita otimização local e protege metas da unidade.',
    { [L3]: 'Semanal', [L4]: 'Semanal', [L5]: 'Semanal' },
    'avancada',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  'n5-m-01': e(
    [
      'Revisar mapa Hoshin (objetivos estratégicos → iniciativas → KPIs).',
      'Avaliar progresso vs meta anual de cada breakthrough.',
      'Identificar gaps e realocar recursos se necessário.',
      'Validar desdobramento até a gemba (catchball).',
      'Atualizar x-matrix e comunicar prioridades corporativas.',
    ],
    [
      'Objetivos estratégicos com KPI mensurável',
      'Iniciativas vinculadas a objetivos',
      'Progresso documentado vs meta',
      'Desdobramento até operação verificado',
    ],
    ['Atingimento metas estratégicas', 'Breakthrough objectives'],
    ['OEE corporativo', 'Margem operacional'],
    'Hoshin conecta estratégia à execução diária — garante que a operação trabalhe nos objetivos certos.',
    { [L4]: 'Mensal', [L5]: 'Mensal' },
    'excelencia',
    { disciplina: 'alto', aderenciaPadrao: 'medio', resultado: 'muito_alto' },
  ),

  ...LEAN_LIBRARY_ENRICHMENT_PHASE2,
};

/** IDs enriquecidos na Fase 1 (prioritários). */
export const LEAN_LIBRARY_ENRICHMENT_PHASE1_IDS = [
  'n1-d-01', 'n1-d-03', 'n1-d-04', 'n1-d-05', 'n1-d-06', 'n1-d-07', 'n1-d-08',
  'n1-d-09', 'n1-d-10', 'n1-d-11', 'n1-d-12', 'n1-d-14', 'n1-s-02', 'n1-s-04',
  'n1-s-06', 'n1-s-08', 'n2-d-01', 'n2-d-02', 'n2-d-05', 'n2-s-02', 'n2-s-03',
  'n2-s-04', 'n2-s-06', 'n2-s-07', 'n2-s-10', 'n2-s-11', 'n3-s-04', 'n3-s-05',
  'n4-s-01', 'n5-m-01',
];

export const LEAN_LIBRARY_ENRICHMENT_ALL_IDS = Object.keys(LEAN_LIBRARY_ENRICHMENT);
